import RestService from './RestService';
import { ApiRoutes } from './apiRoutes';
import { createLogger } from 'shared/logger';

const log = createLogger('JwtApi');

/**
 * Result of JWT token verification
 */
export interface JwtVerifyResult {
  success: boolean;
  jwtSession?: string;
  payload?: any;
}

/**
 * Service for making JWT-related API calls
 * This service is separate from JwtAuthService to prevent circular dependencies
 */
export const JwtApiService = {
  /**
   * Verify a JWT token with the server
   * Sends token in POST body to support large tokens that don't fit in headers
   * @param token JWT token to verify
   * @returns Promise resolving to an object with success flag, jwtSession, and optional payload
   */
  verifyToken: async (token: string): Promise<JwtVerifyResult> => {
    try {
      const response = await RestService.post<{ jwtSession: string; payload?: any }>(
        ApiRoutes.VERIFY,
        { 'Jwt-Jwks': token }
      );

      return {
        success: true,
        jwtSession: response.jwtSession,
        payload: response.payload
      };
    } catch (error) {
      log.error('token verify request failed', { error });
      return { success: false };
    }
  },

  logout: async () => {
    try {
      await RestService.post(ApiRoutes.LOGOUT);
    } catch (error) {
      log.error('logout request failed', { error });
    }
  },

  ttl: async (): Promise<number> => {
    try {
      const response = await RestService.get(ApiRoutes.TTL);
      // Ensure we return a number by using parseInt or Number conversion
      return typeof response === 'object' && response !== null && 'ttl' in response
        ? Number(response.ttl)
        : -2;
    } catch (error) {
      // a failed request (network blip) is not an ended session: let the caller keep the session
      log.error('ttl request failed', { error });
      throw error;
    }
  },
};

export default JwtApiService; 